'use strict'

import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import sites from '../../../config/sites-north-america.js'
const DEFAULT_BRAND = 'garage'

/**
 * Retrieves the root category ID for a specific brand within a site.
 *
 * @param {string} siteId - The ID of the site to search within.
 * @param {string} brandId - The ID of the brand to find within the site.
 * @returns {string|null} The root category ID of the brand if found, otherwise null.
 */
const getRootCategoryId = (siteId, brandId) => {
    // Find the site by ID
    const config = getConfig()
    const site = sites.find((site) => site.id === siteId)

    if (!site) {
        console.error(`Site with ID ${siteId} not found.`)
        return null
    }

    // Find the brand by ID within the site's brands
    const brand = site.l10n.brands.find((brand) => brand.id === brandId)

    if (!brand) {
        console.error(`Brand with ID ${brandId} not found in site ${siteId}.`)
        return null
    }

    // Return the root category ID — prefer per-site value from sites.js over global env var fallback
    return brand.rootCategory || config.app[`ROOT_CATEGORY_ID_${brandId.toUpperCase()}`]
}

const getBrandDisplayName = (brandId, alias = 'ca') => {
    return `${
        brandId?.charAt(0)?.toUpperCase() + brandId?.slice(1)
    } Clothing ${alias?.toUpperCase()}`
}

/**
 * Safely parses BRAND_HOST_MAP from runtime config.
 * Accepts either a JSON string or an already-parsed object.
 *
 * @param {string|Object|null|undefined} value
 * @returns {Object|null}
 */
const parseBrandHostMap = (value) => {
    if (!value) return null
    if (typeof value === 'object') return value
    if (typeof value !== 'string') return null

    try {
        const parsed = JSON.parse(value)
        return parsed && typeof parsed === 'object' ? parsed : null
    } catch (_) {
        return null
    }
}

/**
 * Normalizes a host input (hostname or URL) into a lowercase hostname.
 * Strips protocol, port, and path when needed.
 *
 * @param {string} input
 * @returns {{host: string}}
 */
const normalizeHostInput = (input = '') => {
    const trimmed = String(input).toLowerCase().trim()
    if (!trimmed) {
        return {host: ''}
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        try {
            const url = new URL(trimmed)
            return {host: url.hostname.toLowerCase()}
        } catch (_) {
            // fall through to best-effort parsing
        }
    }

    const withoutPath = trimmed.split('/')[0]
    const [hostname] = withoutPath.split(':')
    return {host: (hostname || '').toLowerCase()}
}

/**
 * Returns current browser hostname when available.
 * Safe for SSR/test environments where window may be undefined.
 *
 * @returns {string}
 */
const getCurrentHostname = () => {
    if (typeof window !== 'undefined' && window.location?.hostname) {
        return window.location.hostname
    }

    return ''
}

/**
 * Resolves a brand ID from hostname using BRAND_HOST_MAP first,
 * then falls back to keyword matching (dynamite/garage),
 * then to DEFAULT_BRAND.
 *
 * @param {string|Object|null|undefined} brandHostMapValue
 * @param {string} hostname
 * @returns {string}
 */
const resolveBrandFromHost = (brandHostMapValue) => {
    const hostname = getCurrentHostname()
    const {host} = normalizeHostInput(hostname)
    const parsedHostMap = parseBrandHostMap(brandHostMapValue)

    if (host && parsedHostMap) {
        for (const [brandId, configuredUrl] of Object.entries(parsedHostMap)) {
            try {
                const configuredHost = new URL(configuredUrl).hostname.toLowerCase()
                if (host === configuredHost || host.includes(configuredHost)) {
                    return brandId
                }
            } catch (_) {
                if (host.includes(String(configuredUrl).toLowerCase())) {
                    return brandId
                }
            }
        }
    }

    // Keep fallback behavior aligned with brand-context (name or short code).
    if (host.includes('dynamite') || host.includes('dyn')) return 'dynamite'
    if (host.includes('garage') || host.includes('grg')) return 'garage'

    return DEFAULT_BRAND
}

export {getRootCategoryId, getBrandDisplayName, resolveBrandFromHost}
